/**
 * Client-side resume text extraction — parsed text is sent to D1 (R2 optional).
 */

export async function extractTextFromFile(file) {
  if (!file) return '';
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.pdf')) return extractPdfText(file);
  if (name.endsWith('.docx')) return extractDocxText(file);
  if (name.endsWith('.doc')) {
    throw new Error(
      'Legacy .doc files are not parsed in-browser. Save as .docx or upload a PDF so Resume Builder can read it.'
    );
  }
  return readAsText(file);
}

function readAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

/**
 * pdf.js v5+ ships workers as .mjs only. Prefer a locally served worker
 * (copied to public/ by postinstall); fall back to the matching CDN .mjs URL.
 */

async function resolvePdfWorkerSrc(version) {
  const local = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
  const cdn = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.mjs`;
  try {
    const res = await fetch(local, { method: 'HEAD' });
    if (res.ok) return local;
  } catch {
    // fall through to CDN
  }
  return cdn;
}

async function extractPdfText(file) {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
  const version = pdfjs.version || '6.0.227';
  // pdf.js v5+ workers are .mjs only — the previous .js CDN path 404s.
  pdfjs.GlobalWorkerOptions.workerSrc = await resolvePdfWorkerSrc(version);

  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjs.getDocument({ data }).promise;

  const parts = [];
  for (let i = 1; i <= doc.numPages; i += 1) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items.map((item) => item.str).join(' ');
    parts.push(pageText);
  }
  return parts.join('\n\n').trim();
}

async function extractDocxText(file) {
  const mammothMod = await import('mammoth');
  const mammoth = mammothMod.default || mammothMod;
  if (typeof mammoth.extractRawText !== 'function') {
    throw new Error('DOCX parser failed to load. Refresh the page and try again.');
  }
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return (result.value || '').trim();
}

export const DEFAULT_SECTION_LABELS = {
  contact: 'Contact',
  summary: 'Professional summary',
  experience: 'Experience',
  education: 'Education',
  skills: 'Skills',
  certifications: 'Certifications',
  languages: 'Languages',
  projects: 'Projects',
};

export function emptyStructuredContent() {
  return {
    contact: { name: '', email: '', phone: '', location: '', linkedin: '' },
    section_labels: { ...DEFAULT_SECTION_LABELS },
    summary: '',
    experience: [],
    education: [],
    skills: [],
    certifications: [],
    languages: [],
    projects: [],
    links: [],
    custom_sections: [],
  };
}

export function newCustomSectionId() {
  return `sec_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
}

export function newExperienceId() {
  return `exp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
}

export function newEducationId() {
  return `edu_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
}

export function newCertificationId() {
  return `cert_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
}

export function newLanguageId() {
  return `lang_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
}

export function newProjectId() {
  return `proj_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
}

export function newLinkId() {
  return `link_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
}

/** Empty resume-like sections for a candidate profile. */
export function emptyProfileResumeSections() {
  return {
    skills: [],
    work_experience: [],
    education_history: [],
    certifications: [],
    languages: [],
    projects: [],
    links: [],
  };
}

/** Map a resume (structured or legacy) into profile section fields. */
export function profileSectionsFromResumeClient(resume) {
  let structured = {};
  if (resume?.structured_content) {
    try {
      structured =
        typeof resume.structured_content === 'string'
          ? JSON.parse(resume.structured_content)
          : resume.structured_content;
    } catch {
      structured = {};
    }
  }
  let skills = Array.isArray(structured.skills) ? structured.skills : [];
  if (!skills.length && resume?.skills) {
    if (Array.isArray(resume.skills)) skills = resume.skills;
    else if (typeof resume.skills === 'string') {
      try {
        const p = JSON.parse(resume.skills);
        skills = Array.isArray(p) ? p : resume.skills.split(',').map((s) => s.trim()).filter(Boolean);
      } catch {
        skills = resume.skills.split(',').map((s) => s.trim()).filter(Boolean);
      }
    }
  }
  return {
    summary: structured.summary || resume?.summary || '',
    skills,
    work_experience: Array.isArray(structured.experience) ? structured.experience : [],
    education_history: Array.isArray(structured.education) ? structured.education : [],
    certifications: Array.isArray(structured.certifications) ? structured.certifications : [],
    languages: Array.isArray(structured.languages) ? structured.languages : [],
    projects: Array.isArray(structured.projects) ? structured.projects : [],
    links: Array.isArray(structured.links) ? structured.links : [],
    years_of_experience:
      resume?.experience_years != null ? resume.experience_years : undefined,
  };
}
